import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import { Tooltip } from "antd";
import {
  BooleanFilter,
  CustomTooltip,
  DateFilter,
  TableAbstract,
  TextFilter,
} from "shared/components/Components";
import { useMercuryContext } from "../../context";
import TableTitle from "../../components/table/TableTitle";
import TimezoneConverter from "components/timezone-converter";
import CostCodeRequest from "./CostCodeRequest";
import { checkPermissions } from "../../common/utils/checkPermissions";
import GetActiveStatusIcon from "shared/components/GetActiveStatusIcon";
import PermissionWrapper from "shared/components/PermissionWrapper";

class ManageCostCode extends TableAbstract<any> {
  FilterComponents = [
    {
      id: "code",
      name: "code",
      title: "Title",
      component: TextFilter,
      active: true,
    },
    {
      id: "validFrom",
      name: "validFrom",
      title: "Valid From",
      component: DateFilter,
      active: true,
    },
    {
      id: "createdAt",
      name: "createdAt",
      title: "Create Date",
      component: DateFilter,
      active: true,
    },
    {
      id: "isActive",
      name: "isActive",
      title: "Active",
      component: BooleanFilter,
      active: true,
    },
  ];

  defaultSortField = "code";

  columns = [
    {
      id: "code",
      title: "Title",
      dataIndex: "code",
      sorter: true,
      width: 150,
      render: (text: string) => <CustomTooltip text={text} length={17} />,
    },
    {
      id: "validFrom",
      title: (
        <Tooltip title="Applies to projects received on or after this date; the cost-code rules pick the code in date. Not set = from the beginning.">
          <span>Valid From</span>
        </Tooltip>
      ) as any,
      dataIndex: "validFrom",
      sorter: true,
      width: 140,
      render: (text?: string | null) =>
        text ? <TimezoneConverter date={text} format="DD MMM YYYY" /> : <i className="muted">Not set</i>,
    },
    {
      id: "createdAt",
      title: "Create Date",
      dataIndex: "createdAt",
      sorter: true,
      width: 200,
      render: (text: string) => <TimezoneConverter date={text} />,
    },
    {
      id: "updatedAt",
      title: "Last Update Date",
      dataIndex: "updatedAt",
      sorter: true,
      width: 200,
      render: (text: string) => <TimezoneConverter date={text} />,
    },
    {
      id: "isActive",
      title: (<TableTitle title="Active" />) as any,
      dataIndex: "isActive",
      sorter: true,
      width: 60,
      align: "center" as any,
      render: (value: boolean | null | undefined) => <GetActiveStatusIcon status={value}/>         
    },
  ];

  renderActions() {
    const userPermissions = this.userContext.userDetails?.permissions || [];
    const hasEditAccess = checkPermissions(userPermissions, [
      {
        customerApplication: "Google",
        permissionName: "Cost Code",
        action: "edit",
      },
    ]);

    const hasCreateAccess = checkPermissions(userPermissions, [
      {
        customerApplication: "Google",
        permissionName: "Cost Code",
        action: "create",
      },
    ]);

    return (
      <>
        <PermissionWrapper
          hasPermissions={hasCreateAccess}
          children={
            <CostCodeRequest
            hasPermissions={hasCreateAccess}
                buttonLabel="Add Cost Code"
                refresh={() => this.reload()}
              />
          }
        />
        <PermissionWrapper
          {...(hasEditAccess && {
            tooltip:
              this.state.selectedRows.length !== 1
                ? "Select 1 row to enable edit option"
                : "",
          })}
          hasPermissions={hasEditAccess && this.state.selectedRows.length === 1}
        >
           <div>
            <CostCodeRequest
                initialValues={this.state.selectedRows[0]}
                buttonLabel="Edit"
                refresh={() => this.reload()}
                hasPermissions={hasEditAccess && this.state.selectedRows.length === 1}
              />
          </div>          
        </PermissionWrapper>
        </>
    );
  }

  pageTitle = "Manage Cost Codes - Mercury © RWS";
  title = "Manage Cost Codes";
  pageClass = "costCodeTable";

  async fetch() {
    const { sortOrder, sortField, currentPage, itemsPerPage } = this.state;

    try {
      const requestPayload = {
        filter: this.filtersToQuery(),
        pagination: {
          pageNumber: currentPage || 1,
          resultsPerPage: itemsPerPage,
        },
        sort: {
          [sortField || "createdAt"]: sortOrder === "ascend" ? 1 : -1,
        },
      };

      const response = await this.userContext.invoicingClient.post("/invoicing-v1/cost-codes/search", requestPayload);

      return { data: response.data } as any;
    } catch (error) {
      console.error("Error fetching cost codes:", error);
      return {
        data: { documents: [] },
        meta: {
          total: 0,
          page: 1,
          pages: 0,
          match: {},
          sort: {},
          skip: 0,
          limit: 10,
        },
      };
    }
  }
}

const ManageCostCodeTable = () => {
  const context = useMercuryContext();
  const breaks = useBreakpoint();

  return (
    <ManageCostCode
      id="ManageCostCode"
      context={context}
      breaks={breaks as any}
      useLegacyEndpoints={false}
      hasSidebar={true}
    />
  );
};

export default ManageCostCodeTable;
